import { AlertTriangle, CheckCircle2, Clock, ListChecks } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { ConsolidatedActionRow, KpiMetricsOutput } from '@/types'
import { KpiStatCard } from './shared/kpi-stat-card'
import { ModuleTag } from './shared/module-tag'
import {
	ResponsiveTable,
	type ResponsiveTableColumn,
} from './shared/responsive-table'
import { StatusPill } from './shared/status-pill'

const ORIGIN_LABEL: Record<string, string> = {
	VEREAGIR: 'Ver e Agir',
	INTERACAO: 'Interação',
	DIRECIONADA: 'Direcionada',
	CARTAO: 'Cartão Amarelo',
}

const DONE_STATUSES = new Set([
	'RESOLVED',
	'COMPLETED',
	'CONGRATULATED',
	'ORIENTED',
])
const IN_PROGRESS_STATUSES = new Set(['IN_PROGRESS'])
const LATE_STATUSES = new Set(['ATRASADO', 'OVERDUE'])

function buildTableColumns(
	t: (key: string) => string,
): ResponsiveTableColumn<ConsolidatedActionRow>[] {
	return [
		{
			key: 'origin',
			header: t('kpiMetrics.acoesConsolidadas.tableOrigin'),
			render: (row) => (
				<ModuleTag
					origin={row.origin}
					label={ORIGIN_LABEL[row.origin] ?? row.origin}
				/>
			),
			hideOnCard: true,
		},
		{
			key: 'date',
			header: t('kpiMetrics.vereAgir.tableDate'),
			render: (row) =>
				new Date(row.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' }),
			className: 'whitespace-nowrap',
		},
		{
			key: 'crName',
			header: t('kpiMetrics.vereAgir.tableCr'),
			render: (row) => row.crName,
			className: 'max-w-40 truncate',
		},
		{
			key: 'responsibleName',
			header: t('kpiMetrics.vereAgir.tableResponsible'),
			render: (row) => row.responsibleName,
			className: 'max-w-40 truncate',
		},
		{
			key: 'item',
			header: t('kpiMetrics.acoesConsolidadas.tableItem'),
			render: (row) => row.item,
			className: 'max-w-96 truncate',
		},
		{
			key: 'status',
			header: t('kpiMetrics.vereAgir.tableStatus'),
			render: (row) => <StatusPill status={row.status} />,
			hideOnCard: true,
		},
	]
}

export function AcoesConsolidadasDashboard({
	data,
}: {
	data: KpiMetricsOutput
}) {
	const { t } = useTranslation()
	const rows = data.consolidatedActions
	const tableColumns = buildTableColumns(t)

	const stats = useMemo(() => {
		let done = 0
		let inProgress = 0
		let late = 0
		for (const row of rows) {
			if (DONE_STATUSES.has(row.status)) done++
			else if (IN_PROGRESS_STATUSES.has(row.status)) inProgress++
			else if (LATE_STATUSES.has(row.status)) late++
		}
		return { total: rows.length, done, inProgress, late }
	}, [rows])

	const resolutionRate =
		stats.total > 0 ? Math.round((stats.done / stats.total) * 100) : 0

	return (
		<div className="flex flex-col gap-4">
			<div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
				<KpiStatCard
					icon={<ListChecks className="size-4" />}
					iconClass="bg-kaefer-sapphire-blue/15 text-kaefer-sapphire-blue"
					label={t('kpiMetrics.acoesConsolidadas.total')}
					value={String(stats.total)}
				/>
				<KpiStatCard
					icon={<CheckCircle2 className="size-4" />}
					iconClass="bg-kaefer-grass-green/15 text-kaefer-forest-green"
					label={t('kpiMetrics.acoesConsolidadas.done')}
					value={String(stats.done)}
				/>
				<KpiStatCard
					icon={<Clock className="size-4" />}
					iconClass="bg-kaefer-light-silver/60 text-kaefer-medium-grey"
					label={t('kpiMetrics.acoesConsolidadas.inProgress')}
					value={String(stats.inProgress)}
				/>
				<KpiStatCard
					icon={<AlertTriangle className="size-4" />}
					iconClass="bg-kaefer-red/15 text-kaefer-red"
					label={t('kpiMetrics.acoesConsolidadas.late')}
					value={String(stats.late)}
					sub={`${resolutionRate}% ${t('kpiMetrics.acoesConsolidadas.resolutionRate')}`}
				/>
			</div>

			<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
				<span className="text-sm font-semibold text-kaefer-dark-grey">
					{t('kpiMetrics.acoesConsolidadas.table')}
				</span>
				{rows.length === 0 ? (
					<p className="py-10 text-center text-sm text-muted-foreground">
						{t('kpiMetrics.charts.noData')}
					</p>
				) : (
					<div className="mt-2 max-h-128 overflow-auto">
						<ResponsiveTable
							columns={tableColumns}
							rows={rows}
							rowKey={(row, index) => `${row.origin}-${row.crId}-${index}`}
							cardTitleKey="item"
							cardBadge={(row) => <StatusPill status={row.status} />}
						/>
					</div>
				)}
			</div>
		</div>
	)
}
